import React, { useEffect, useState } from "react";
import { usePhrase } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";

function ConsentList({ list, isActive, onWithdraw }) {
  const pt = usePhrase();
  return (
    <div className="space-y-3">
      {list.map((c) => (
        <div key={c.id} className="p-4 rounded-md border border-slate-200 bg-white flex items-start gap-4" data-testid={`consent-${c.id}`}>
          <div className={`w-8 h-8 rounded-md grid place-items-center text-white shrink-0 ${isActive ? "bg-emerald-500" : "bg-slate-500"}`}>
            {isActive ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 brand-heading">{pt(c.consent_type)}</div>
            <div className="text-xs text-slate-500 mt-0.5">{pt("Purpose")}: {pt(c.purpose) || "—"} · {pt("Version")}: {c.version}</div>
            <div className="text-xs text-slate-500 mt-0.5">{pt("Data categories")}: {(c.data_categories || []).map(pt).join(", ") || "—"}</div>
            <div className="text-xs text-slate-500 mt-0.5">{pt("Granted")}: {new Date(c.granted_at).toLocaleString(localStorage.getItem("lang") === "hi" ? "hi-IN" : "en-IN")}</div>
          </div>
          {isActive && (
            <button onClick={() => onWithdraw(c.id)} data-testid={`withdraw-${c.id}`} className="text-xs text-red-600 hover:text-red-700 font-semibold">{pt("Withdraw")}</button>
          )}
        </div>
      ))}
      {list.length === 0 && <div className="text-sm text-slate-500">{pt("Nothing here yet.")}</div>}
    </div>
  );
}

export default function ConsentCentre() {
  const pt = usePhrase();
  const [items, setItems] = useState([]);
  const load = async () => setItems((await api.get("/consents")).data);
  useEffect(() => { load(); }, []);

  const withdraw = async (id) => {
    try {
      await api.post(`/consents/${id}/withdraw`);
      toast.success(pt("Consent withdrawn"));
      await load();
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("Consent withdraw failed", e);
      toast.error(pt("Could not withdraw consent — please try again."));
    }
  };
  const active = items.filter((i) => i.status === "granted");
  const historic = items.filter((i) => i.status !== "granted");

  return (
    <div className="space-y-6" data-testid="consent-centre">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 brand-heading">{pt("Consent Centre")}</h1>
        <p className="text-slate-600 mt-1 text-sm">{pt("Manage which lenders have access to your data. Withdrawal does not erase legally required transaction records.")}</p>
      </div>
      <div>
        <h2 className="font-semibold text-slate-900 mb-3">{pt("Active consents")}</h2>
        <ConsentList list={active} isActive onWithdraw={withdraw} />
      </div>
      <div>
        <h2 className="font-semibold text-slate-900 mb-3">{pt("Expired / withdrawn consents")}</h2>
        <ConsentList list={historic} isActive={false} onWithdraw={withdraw} />
      </div>
      <DisclaimerBanner compact />
    </div>
  );
}
